import { LocationLink } from '@/components/shared/LocationLink'
import { useReverseGeocode } from '@/hooks/useReverseGeocode'
import { parseCoordinates } from '@/utils/orderSurveyDisplay'
import { parseLeadingCoords } from '@/utils/geo'

interface GeocodedLocationLinkProps {
  /** Display label. May be a real address or a raw "lat, lng" string. */
  location: string
  /** Explicit maps link to preserve as the link target. */
  href?: string | null
  className?: string
  /** Explicit coordinates. When omitted, they're parsed from `location`. */
  lat?: number | null
  lng?: number | null
  /** When true, the label wraps to multiple lines instead of truncating. */
  wrap?: boolean
}

/**
 * Drop-in replacement for {@link LocationLink} that reverse-geocodes
 * coordinates into a readable address.
 *
 * When coordinates are available (explicit `lat`/`lng`, or a coordinate-like
 * `location` string), the resolved address is shown once loaded, falling back
 * to a clean `"lat, lng"` label while loading or if geocoding fails. For plain
 * address labels it renders {@link LocationLink} unchanged.
 */
export function GeocodedLocationLink({
  location,
  href,
  className,
  lat,
  lng,
  wrap = false,
}: GeocodedLocationLinkProps) {
  const explicit =
    typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : null
  const parsed = explicit ?? parseCoordinates(location) ?? parseLeadingCoords(location)

  const { address, city } = useReverseGeocode(parsed?.lat, parsed?.lng)

  // No coordinates to resolve → behave exactly like LocationLink.
  if (!parsed) {
    return <LocationLink location={location} href={href} className={className} wrap={wrap} />
  }

  // `address` (formatted_address) already includes the city, so prefer it;
  // fall back to city, then to a clean coordinate label (not the raw string,
  // which may contain backend `-` placeholders).
  const cleanCoordLabel = `${parsed.lat}, ${parsed.lng}`
  const label = address || city || cleanCoordLabel

  return <LocationLink location={label} href={href} className={className} wrap={wrap} />
}
